import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { connectionsApi } from '../services/api/connections';
import { Partner } from '../types/partner';
import { TransferMethod, AuthMethod, CreateConnectionRequest, HostingType, HttpAuthType } from '../types/connection';
import { useAutoSuggestName } from '../hooks/useAutoSuggestName';

interface CreateConnectionProps {
  partners: Partner[];
  onClose: () => void;
  onCreate: () => void;
}

/**
 * T081: CreateConnection form with name field last and auto-suggest
 * T083: Auto-suggest preview in ConnectionForm
 * T085: "Use suggested name" button in ConnectionForm
 * T088: Direction dropdown derives behavior from connection hosting type
 */
function CreateConnection({ partners, onClose, onCreate }: CreateConnectionProps) {
  const [formData, setFormData] = useState<CreateConnectionRequest>({
    name: '',
    partnerId: '',
    hostingType: 'hosted',           // FR-023: hosted = partner connects to us (default)
    transferMethod: 'sftp',
    config: {
      auth_method: 'key',
      port: 22,
      keyType: 'ecdsa',
      enable_encryption: true,
      enable_compression: false,
      timeout: 30,
      retry_attempts: 3
    }
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // T025: HTTP-specific state for custom OAuth2 params
  const [customParams, setCustomParams] = useState<Record<string, string>>({});

  // T079: Auto-suggest name hook
  const { generateSuggestedName, hasRequiredContext } = useAutoSuggestName({ type: 'connection' });

  // Get selected partner name for auto-suggest
  const selectedPartner = useMemo(() => {
    return partners.find(p => p.partnerId === formData.partnerId);
  }, [partners, formData.partnerId]);

  // T083: Generate suggested name based on current selections
  const suggestedName = useMemo(() => {
    return generateSuggestedName({
      partnerName: selectedPartner?.name,
      transferMethod: formData.transferMethod,
      hostingType: formData.hostingType,
    });
  }, [generateSuggestedName, selectedPartner?.name, formData.transferMethod, formData.hostingType]);

  // Check if we have all required context for a suggestion
  const canSuggestName = useMemo(() => {
    return hasRequiredContext({
      partnerName: selectedPartner?.name,
      transferMethod: formData.transferMethod,
      hostingType: formData.hostingType,
    });
  }, [hasRequiredContext, selectedPartner?.name, formData.transferMethod, formData.hostingType]);

  // T088: Get hosting type help text based on selection
  const hostingTypeHelpText = useMemo(() => {
    if (formData.hostingType === 'hosted') {
      return 'Partner connects to our SFTP server. Typical use: Receive files from partner (inbound) or allow partner to download files (send).';
    }
    // Remote: we connect out to the partner. Only SFTP can be hosted; HTTP/API + Azure Blob
    // are remote-only, so word the help text generically for those.
    if (formData.transferMethod === 'api') {
      return 'We connect to the partner\'s HTTP/API endpoint (remote-only). Typical use: pick up from or deliver to the partner.';
    }
    if (formData.transferMethod === 'azure_blob') {
      return 'We connect to the partner\'s Azure Blob container (remote-only). Typical use: pick up from or deliver to the partner.';
    }
    return 'We connect to partner\'s SFTP server. Typical use: Pick up files from partner or drop files to partner.';
  }, [formData.hostingType, formData.transferMethod]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      // Validate form
      const validationErrors: Record<string, string> = {};

      if (!formData.name.trim()) {
        validationErrors.name = 'Connection name is required';
      }

      if (!formData.partnerId) {
        validationErrors.partnerId = 'Partner is required';
      }

      const isRemote = formData.hostingType === 'remote';
      const isSftp = formData.transferMethod === 'sftp';
      const isHttp = formData.transferMethod === 'api';
      const isAzure = formData.transferMethod === 'azure_blob';

      // Remote SFTP validation
      if (isRemote && isSftp) {
        if (!formData.config.host?.trim()) {
          validationErrors.host = 'Host is required for remote SFTP connections';
        }
        if (!formData.config.port || formData.config.port < 1 || formData.config.port > 65535) {
          validationErrors.port = 'Valid port number is required';
        }
        if (!formData.config.username?.trim()) {
          validationErrors.username = 'Username is required for remote SFTP connections';
        }
        if (formData.config.auth_method === 'password' && !formData.config.password?.trim()) {
          validationErrors.password = 'Password is required when using password authentication';
        }
        // 018-thub-5: Only require private key when "Use existing key" is toggled on
        if (formData.config.auth_method === 'key' && formData.config.useExistingKey && !formData.config.private_key_content?.trim()) {
          validationErrors.private_key_content = 'Private key is required when using an existing key';
        }
      }

      // Remote HTTP/API validation
      if (isRemote && isHttp) {
        if (!formData.config.baseUrl?.trim()) {
          validationErrors.baseUrl = 'Base URL is required for HTTP connections';
        } else if (!formData.config.baseUrl.startsWith('https://')) {
          validationErrors.baseUrl = 'Base URL must use HTTPS';
        }

        const authType = formData.config.httpAuthType || 'none';
        if (authType === 'basic') {
          if (!formData.config.username?.trim()) {
            validationErrors.username = 'Username is required for Basic Auth';
          }
          if (!formData.config.password?.trim()) {
            validationErrors.password = 'Password is required for Basic Auth';
          }
        } else if (authType === 'bearer') {
          if (!formData.config.bearerToken?.trim()) {
            validationErrors.bearerToken = 'Bearer token is required';
          }
        } else if (authType === 'oauth2_cc' || authType === 'oauth2_custom') {
          if (!formData.config.oauth2ClientId?.trim()) {
            validationErrors.oauth2ClientId = 'Client ID is required for OAuth2';
          }
          if (!formData.config.oauth2ClientSecret?.trim()) {
            validationErrors.oauth2ClientSecret = 'Client Secret is required for OAuth2';
          }
          if (!formData.config.oauth2TokenEndpoint?.trim()) {
            validationErrors.oauth2TokenEndpoint = 'Token Endpoint is required for OAuth2';
          }
        }
      }

      // THUB-220: Azure Blob validation (always remote). Container URL shape + SAS presence.
      if (isAzure) {
        const url = formData.config.azureContainerUrl?.trim();
        if (!url) {
          validationErrors.azureContainerUrl = 'Container URL is required for Azure Blob connections';
        } else if (!url.startsWith('https://')) {
          validationErrors.azureContainerUrl = 'Container URL must use HTTPS';
        } else if (!/^https:\/\/[^/]+\.blob\.core\.windows\.net\/[^/]+/.test(url)
                   && !/^https:\/\/[^/]+\.cgic-internal\.com\/[^/]+/.test(url)) {
          // Prod: *.blob.core.windows.net. Also allow the internal DEV Azurite validation
          // host (*.cgic-internal.com) — no real partner uses our internal domain. The
          // backend enforces the authoritative, env-gated host check.
          validationErrors.azureContainerUrl =
            'Expected https://<account>.blob.core.windows.net/<container>[/prefix]';
        }
        if (!formData.config.sasToken?.trim()) {
          validationErrors.sasToken = 'SAS token is required for Azure Blob connections';
        }
      }

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        setLoading(false);
        return;
      }

      await connectionsApi.createConnection(formData);

      // Show success toast and close modal - note: resources are still being provisioned
      toast.success(`Connection "${formData.name}" is being set up`);
      onCreate();
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create connection';
      setErrors({ general: errorMessage });
      toast.error(`Failed to create connection: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (field.startsWith('config.')) {
      const configField = field.replace('config.', '');
      setFormData(prev => ({
        ...prev,
        config: {
          ...prev.config,
          [configField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Hosting Type applies only to SFTP (hosted = partner connects to our Transfer Family
  // server). HTTP/API and Azure Blob are ALWAYS remote — THUB is the client connecting out —
  // and the backend force-sets hosting_type=remote for them regardless. So when the method is
  // api or azure_blob, lock hosting to remote (and hide the Hosted option below).
  const isRemoteOnlyMethod = (m: TransferMethod) => m === 'api' || m === 'azure_blob';

  const handleTransferMethodChange = (method: TransferMethod) => {
    setFormData(prev => ({
      ...prev,
      transferMethod: method,
      hostingType: isRemoteOnlyMethod(method) ? 'remote' : prev.hostingType,
    }));
    if (errors.transferMethod) setErrors(prev => ({ ...prev, transferMethod: '' }));
  };

  // T085: Handle "Use suggested name" button click
  const handleUseSuggestedName = () => {
    if (canSuggestName && suggestedName) {
      setFormData(prev => ({
        ...prev,
        name: suggestedName
      }));
      // Clear any name error
      if (errors.name) {
        setErrors(prev => ({ ...prev, name: '' }));
      }
    }
  };

  // Common input styles
  const inputBaseClass = "w-full px-3 py-2.5 border rounded text-sm transition-colors bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25";
  const inputNormalClass = `${inputBaseClass} border-slate-300 dark:border-slate-600`;
  const inputErrorClass = `${inputBaseClass} border-danger-500`;
  const labelClass = "block mb-1 font-medium text-slate-900 dark:text-slate-100 text-sm";
  const errorTextClass = "block mt-1 text-xs text-danger-500";
  const smallTextClass = "block mt-1 text-xs text-slate-500 dark:text-slate-400";
  const formSectionClass = "border border-slate-200 dark:border-slate-600 rounded-lg p-5 bg-slate-50 dark:bg-slate-700/50";
  const formSectionTitleClass = "m-0 mb-4 text-slate-900 dark:text-slate-100 text-base font-semibold";

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex justify-center items-center z-[1000]">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg relative w-[90%] max-w-[800px] max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-5 pt-5 mb-5">
          <h2 className="m-0 text-slate-900 dark:text-slate-100">Create New Connection</h2>
          <button
            className="bg-transparent border-none text-2xl cursor-pointer text-slate-500 dark:text-slate-400 p-0 w-[30px] h-[30px] flex items-center justify-center hover:text-slate-900 dark:hover:text-slate-100"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5 px-5 pb-5">
          {errors.general && (
            <div className="px-4 py-3 bg-danger-500/10 text-danger-500 border border-danger-500 rounded">
              {errors.general}
            </div>
          )}

          {/* T081: Partner, Hosting Type, and Method fields come FIRST */}
          <div className={formSectionClass}>
            <h3 className={formSectionTitleClass}>Connection Configuration</h3>

            <div className="mb-4">
              <label htmlFor="partnerId" className={labelClass}>Partner *</label>
              <select
                id="partnerId"
                value={formData.partnerId}
                onChange={(e) => handleInputChange('partnerId', e.target.value)}
                className={errors.partnerId ? inputErrorClass : inputNormalClass}
              >
                <option value="">Select a partner</option>
                {partners.map(partner => (
                  <option key={partner.partnerId} value={partner.partnerId}>
                    {partner.name}
                  </option>
                ))}
              </select>
              {errors.partnerId && <span className={errorTextClass}>{errors.partnerId}</span>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="mb-4">
                <label htmlFor="hostingType" className={labelClass}>Hosting Type *</label>
                <select
                  id="hostingType"
                  value={formData.hostingType}
                  onChange={(e) => {
                    const hostingType = e.target.value as HostingType;
                    setFormData(prev => ({
                      ...prev,
                      hostingType,
                      config: {
                        auth_method: prev.config.auth_method,
                        port: prev.config.port,
                        keyType: prev.config.keyType || 'ecdsa',
                        timeout: prev.config.timeout,
                        retry_attempts: prev.config.retry_attempts,
                        enable_encryption: prev.config.enable_encryption,
                        enable_compression: prev.config.enable_compression,
                      },
                    }));
                  }}
                  className={inputNormalClass}
                  disabled={isRemoteOnlyMethod(formData.transferMethod)}
                >
                  {/* Hosted applies only to SFTP; HTTP/API + Azure Blob are remote-only. */}
                  {!isRemoteOnlyMethod(formData.transferMethod) && (
                    <option value="hosted">Hosted (Partner connects to us)</option>
                  )}
                  <option value="remote">Remote (We connect to partner)</option>
                </select>
                {/* T088: Help text based on hosting type selection */}
                <small className={`${smallTextClass} italic`}>{hostingTypeHelpText}</small>
              </div>

              <div className="mb-4">
                <label htmlFor="transferMethod" className={labelClass}>Transfer Method *</label>
                <select
                  id="transferMethod"
                  value={formData.transferMethod}
                  onChange={(e) => handleTransferMethodChange(e.target.value as TransferMethod)}
                  className={inputNormalClass}
                >
                  <option value="sftp">SFTP</option>
                  <option value="api">HTTP/API</option>
                  <option value="azure_blob">Azure Blob Storage</option>
                </select>
              </div>
            </div>
          </div>

          {/* T019: Connection Name moved here, right after Partner/Hosting/Method */}
          <div className={formSectionClass}>
            <h3 className={formSectionTitleClass}>Connection Name</h3>

            {/* T083: Auto-suggest preview */}
            {canSuggestName && suggestedName && (
              <div className="flex items-center gap-2.5 px-4 py-3 bg-slate-100 dark:bg-slate-600 border border-slate-200 dark:border-slate-500 rounded-md mb-4">
                <span className="text-[13px] text-slate-500 dark:text-slate-300 whitespace-nowrap">Suggested name:</span>
                <span className="text-sm font-medium text-slate-900 dark:text-slate-100 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{suggestedName}</span>
                {/* T085: "Use suggested name" button */}
                <button
                  type="button"
                  className="bg-slate-200 dark:bg-slate-500 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-400 px-3 py-1.5 rounded text-[13px] cursor-pointer whitespace-nowrap transition-colors hover:bg-slate-300 dark:hover:bg-slate-400 hover:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleUseSuggestedName}
                  disabled={formData.name === suggestedName}
                >
                  Use suggested
                </button>
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="name" className={labelClass}>Connection Name *</label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder={canSuggestName ? suggestedName : 'Enter connection name'}
                className={errors.name ? inputErrorClass : inputNormalClass}
              />
              {errors.name && <span className={errorTextClass}>{errors.name}</span>}
              <small className={smallTextClass}>
                Choose a descriptive name for this connection.
                {canSuggestName && ' You can use the suggested name above or enter a custom one.'}
              </small>
            </div>
          </div>

          {/* THUB-220: Azure Blob Storage Configuration Section */}
          {formData.transferMethod === 'azure_blob' && (
            <div className={formSectionClass}>
              <h3 className={formSectionTitleClass}>Azure Blob Storage Configuration</h3>

              <div className="mb-4">
                <label htmlFor="azureContainerUrl" className={labelClass}>Container URL *</label>
                <input
                  /* THUB-287: same clipboard hazard as the SAS — pasted together, and a
                     trailing space yields a host that will not resolve. Visible here, but
                     trimmed for the same reason and in the same place. */
                  id="azureContainerUrl"
                  type="url"
                  value={formData.config.azureContainerUrl || ''}
                  onChange={(e) => handleInputChange('config.azureContainerUrl', e.target.value.trim())}
                  placeholder="https://account.blob.core.windows.net/container"
                  className={errors.azureContainerUrl ? inputErrorClass : inputNormalClass}
                />
                {errors.azureContainerUrl && <span className={errorTextClass}>{errors.azureContainerUrl}</span>}
                <small className={smallTextClass}>
                  https://&lt;account&gt;.blob.core.windows.net/&lt;container&gt;
                </small>
              </div>

              {/* THUB-220: no blob-path field here by design. The connection provides auth +
                  endpoint; the path belongs to the task (Destination/Source Directory), same as
                  SFTP and HTTP. A connection-level prefix applied to every task on the
                  connection, so it could not express per-task destinations. */}

              <div className="mb-4">
                <label htmlFor="sasToken" className={labelClass}>SAS Token *</label>
                {/* THUB-287: trim on CHANGE, not just before submit. The field is
                    type="password", so pasted leading/trailing whitespace is invisible — and
                    because `sig` is conventionally last in a SAS, that whitespace lands inside
                    the signature and Azure returns 403 AuthenticationFailed on a token that is
                    otherwise perfectly valid. Trimming here means the value held in state is
                    already canonical, so submit, validation, and any future test-before-save
                    all see the same clean string rather than each needing its own trim. */}
                <input
                  id="sasToken"
                  type="password"
                  autoComplete="new-password"
                  value={formData.config.sasToken || ''}
                  onChange={(e) => handleInputChange('config.sasToken', e.target.value.trim())}
                  placeholder="?sv=...&sp=...&sig=..."
                  className={errors.sasToken ? inputErrorClass : inputNormalClass}
                />
                {errors.sasToken && <span className={errorTextClass}>{errors.sasToken}</span>}
                <small className={smallTextClass}>
                  Write-only. Stored securely and never displayed again.
                </small>
              </div>
            </div>
          )}

          {/* T025: HTTP/API Configuration Section */}
          {formData.transferMethod === 'api' && (
            <div className={formSectionClass}>
              <h3 className={formSectionTitleClass}>HTTP/API Configuration</h3>

              <div className="mb-4">
                <label htmlFor="baseUrl" className={labelClass}>Base URL *</label>
                <input
                  id="baseUrl"
                  type="url"
                  value={formData.config.baseUrl || ''}
                  onChange={(e) => handleInputChange('config.baseUrl', e.target.value)}
                  placeholder="https://api.partner.com"
                  className={errors.baseUrl ? inputErrorClass : inputNormalClass}
                />
                {errors.baseUrl && <span className={errorTextClass}>{errors.baseUrl}</span>}
                <small className={smallTextClass}>Must be a valid HTTPS URL</small>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="mb-4">
                  <label htmlFor="httpMethod" className={labelClass}>HTTP Method *</label>
                  <select
                    id="httpMethod"
                    value={formData.config.httpMethod || 'POST'}
                    onChange={(e) => handleInputChange('config.httpMethod', e.target.value)}
                    className={inputNormalClass}
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label htmlFor="httpContentType" className={labelClass}>Content Type *</label>
                  <select
                    id="httpContentType"
                    value={formData.config.httpContentType || 'multipart/form-data'}
                    onChange={(e) => handleInputChange('config.httpContentType', e.target.value)}
                    className={inputNormalClass}
                  >
                    <option value="multipart/form-data">multipart/form-data</option>
                    <option value="application/octet-stream">application/octet-stream</option>
                    <option value="application/json">application/json</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label htmlFor="httpTimeout" className={labelClass}>Timeout (seconds) *</label>
                  <input
                    id="httpTimeout"
                    type="number"
                    value={formData.config.httpTimeout || 30}
                    onChange={(e) => handleInputChange('config.httpTimeout', parseInt(e.target.value))}
                    min="1"
                    max="120"
                    className={inputNormalClass}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="httpAuthType" className={labelClass}>Authentication Type *</label>
                <select
                  id="httpAuthType"
                  value={formData.config.httpAuthType || 'none'}
                  onChange={(e) => handleInputChange('config.httpAuthType', e.target.value as HttpAuthType)}
                  className={inputNormalClass}
                >
                  <option value="none">None</option>
                  <option value="basic">Basic Auth</option>
                  <option value="bearer">Bearer Token</option>
                  <option value="oauth2_cc">OAuth2 Client Credentials</option>
                  <option value="oauth2_custom">OAuth2 Custom</option>
                </select>
              </div>

              {/* Basic Auth fields */}
              {formData.config.httpAuthType === 'basic' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="mb-4">
                    <label htmlFor="username" className={labelClass}>Username *</label>
                    <input
                      id="username"
                      type="text"
                      value={formData.config.username || ''}
                      onChange={(e) => handleInputChange('config.username', e.target.value)}
                      placeholder="Enter username"
                      className={errors.username ? inputErrorClass : inputNormalClass}
                    />
                    {errors.username && <span className={errorTextClass}>{errors.username}</span>}
                  </div>
                  <div className="mb-4">
                    <label htmlFor="password" className={labelClass}>Password *</label>
                    <input
                      id="password"
                      type="password"
                      value={formData.config.password || ''}
                      onChange={(e) => handleInputChange('config.password', e.target.value)}
                      placeholder="Enter password"
                      className={errors.password ? inputErrorClass : inputNormalClass}
                    />
                    {errors.password && <span className={errorTextClass}>{errors.password}</span>}
                  </div>
                </div>
              )}

              {/* Bearer Token field */}
              {formData.config.httpAuthType === 'bearer' && (
                <div className="mb-4">
                  <label htmlFor="bearerToken" className={labelClass}>Bearer Token *</label>
                  <input
                    id="bearerToken"
                    type="password"
                    value={formData.config.bearerToken || ''}
                    onChange={(e) => handleInputChange('config.bearerToken', e.target.value)}
                    placeholder="Enter bearer token"
                    className={errors.bearerToken ? inputErrorClass : inputNormalClass}
                  />
                  {errors.bearerToken && <span className={errorTextClass}>{errors.bearerToken}</span>}
                </div>
              )}

              {/* OAuth2 Client Credentials fields */}
              {(formData.config.httpAuthType === 'oauth2_cc' || formData.config.httpAuthType === 'oauth2_custom') && (
                <div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="mb-4">
                      <label htmlFor="oauth2ClientId" className={labelClass}>Client ID *</label>
                      <input
                        id="oauth2ClientId"
                        type="text"
                        value={formData.config.oauth2ClientId || ''}
                        onChange={(e) => handleInputChange('config.oauth2ClientId', e.target.value)}
                        placeholder="Enter client ID"
                        className={errors.oauth2ClientId ? inputErrorClass : inputNormalClass}
                      />
                      {errors.oauth2ClientId && <span className={errorTextClass}>{errors.oauth2ClientId}</span>}
                    </div>
                    <div className="mb-4">
                      <label htmlFor="oauth2ClientSecret" className={labelClass}>Client Secret *</label>
                      <input
                        id="oauth2ClientSecret"
                        type="password"
                        value={formData.config.oauth2ClientSecret || ''}
                        onChange={(e) => handleInputChange('config.oauth2ClientSecret', e.target.value)}
                        placeholder="Enter client secret"
                        className={errors.oauth2ClientSecret ? inputErrorClass : inputNormalClass}
                      />
                      {errors.oauth2ClientSecret && <span className={errorTextClass}>{errors.oauth2ClientSecret}</span>}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="oauth2TokenEndpoint" className={labelClass}>Token Endpoint *</label>
                    <input
                      id="oauth2TokenEndpoint"
                      type="url"
                      value={formData.config.oauth2TokenEndpoint || ''}
                      onChange={(e) => handleInputChange('config.oauth2TokenEndpoint', e.target.value)}
                      placeholder="https://auth.partner.com/oauth/token"
                      className={errors.oauth2TokenEndpoint ? inputErrorClass : inputNormalClass}
                    />
                    {errors.oauth2TokenEndpoint && <span className={errorTextClass}>{errors.oauth2TokenEndpoint}</span>}
                  </div>

                  <div className="mb-4">
                    <label htmlFor="oauth2Scope" className={labelClass}>Scope (optional)</label>
                    <input
                      id="oauth2Scope"
                      type="text"
                      value={formData.config.oauth2Scope || ''}
                      onChange={(e) => handleInputChange('config.oauth2Scope', e.target.value)}
                      placeholder="read:files write:files"
                      className={inputNormalClass}
                    />
                    <small className={smallTextClass}>Space-separated list of OAuth2 scopes</small>
                  </div>

                  {/* OAuth2 Custom Parameters (only for oauth2_custom) */}
                  {formData.config.httpAuthType === 'oauth2_custom' && (
                    <div className="mb-4">
                      <label className={labelClass}>Custom OAuth2 Parameters</label>
                      {Object.entries(customParams).map(([key, value], idx) => (
                        <div key={idx} className="flex gap-2 mb-2">
                          <input
                            placeholder="Key"
                            value={key}
                            onChange={(e) => {
                              const newParams = { ...customParams };
                              delete newParams[key];
                              newParams[e.target.value] = value;
                              setCustomParams(newParams);
                              handleInputChange('config.oauth2CustomParams', newParams);
                            }}
                            className={inputNormalClass}
                          />
                          <input
                            placeholder="Value"
                            value={value}
                            onChange={(e) => {
                              const newParams = { ...customParams, [key]: e.target.value };
                              setCustomParams(newParams);
                              handleInputChange('config.oauth2CustomParams', newParams);
                            }}
                            className={inputNormalClass}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newParams = { ...customParams };
                              delete newParams[key];
                              setCustomParams(newParams);
                              handleInputChange('config.oauth2CustomParams', newParams);
                            }}
                            className="text-red-500 hover:text-red-700 px-3 text-xl"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const newKey = `param${Object.keys(customParams).length + 1}`;
                          const newParams = { ...customParams, [newKey]: '' };
                          setCustomParams(newParams);
                          handleInputChange('config.oauth2CustomParams', newParams);
                        }}
                        className="text-blue-500 hover:text-blue-700 text-sm"
                      >
                        + Add Parameter
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {(formData.hostingType === 'remote' && formData.transferMethod === 'sftp') && (
            <div className={formSectionClass}>
              <h3 className={formSectionTitleClass}>Remote Connection Settings</h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="mb-4">
                  <label htmlFor="host" className={labelClass}>Host *</label>
                  <input
                    id="host"
                    type="text"
                    value={formData.config.host || ''}
                    onChange={(e) => handleInputChange('config.host', e.target.value)}
                    placeholder="partner.example.com"
                    className={errors.host ? inputErrorClass : inputNormalClass}
                  />
                  {errors.host && <span className={errorTextClass}>{errors.host}</span>}
                </div>

                <div className="mb-4">
                  <label htmlFor="port" className={labelClass}>Port *</label>
                  <input
                    id="port"
                    type="number"
                    value={formData.config.port || 22}
                    onChange={(e) => handleInputChange('config.port', parseInt(e.target.value))}
                    min="1"
                    max="65535"
                    className={errors.port ? inputErrorClass : inputNormalClass}
                  />
                  {errors.port && <span className={errorTextClass}>{errors.port}</span>}
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="username" className={labelClass}>Username *</label>
                <input
                  id="username"
                  type="text"
                  value={formData.config.username || ''}
                  onChange={(e) => handleInputChange('config.username', e.target.value)}
                  placeholder="Enter username"
                  className={errors.username ? inputErrorClass : inputNormalClass}
                />
                {errors.username && <span className={errorTextClass}>{errors.username}</span>}
              </div>

              <div className="mb-4">
                <label htmlFor="authMethod" className={labelClass}>Authentication Method *</label>
                <select
                  id="authMethod"
                  value={formData.config.auth_method || 'key'}
                  onChange={(e) => handleInputChange('config.auth_method', e.target.value as AuthMethod)}
                  className={inputNormalClass}
                >
                  <option value="key">SSH Key</option>
                  <option value="password">Password</option>
                </select>
              </div>

              {formData.config.auth_method === 'key' && (
                <div className="mb-4 space-y-3">
                  {/* THUB-113: Key type selector (only when NOT using existing key) */}
                  {!formData.config.useExistingKey && (
                    <div>
                      <label htmlFor="keyType" className={labelClass}>Key Type</label>
                      <select
                        id="keyType"
                        value={formData.config.keyType || 'ecdsa'}
                        onChange={(e) => handleInputChange('config.keyType', e.target.value as 'rsa' | 'ecdsa')}
                        className={inputNormalClass}
                      >
                        <option value="ecdsa">ECDSA P-384 (Recommended)</option>
                        <option value="rsa">RSA 4096-bit</option>
                      </select>
                    </div>
                  )}

                  {/* 018-thub-5: Auto-generation info banner (default) */}
                  {!formData.config.useExistingKey && (
                    <div className="flex items-start gap-3 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <svg className="w-5 h-5 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-blue-800 dark:text-blue-200 m-0">
                          SSH key pair will be generated automatically
                        </p>
                        <p className="text-xs text-blue-600 dark:text-blue-300 mt-1 m-0">
                          {(formData.config.keyType || 'ecdsa') === 'ecdsa'
                            ? 'An ECDSA P-384 key pair will be created when the connection is set up.'
                            : 'An RSA 4096-bit key pair will be created when the connection is set up.'
                          } The public key will be available in the connection detail view for you to share with your partner.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Toggle for "Use existing key" */}
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                    <input
                      type="checkbox"
                      checked={formData.config.useExistingKey || false}
                      onChange={(e) => {
                        handleInputChange('config.useExistingKey', e.target.checked);
                        if (!e.target.checked) {
                          handleInputChange('config.private_key_content', '');
                        }
                      }}
                      className="w-auto accent-primary-600"
                    />
                    Use existing key
                  </label>

                  {/* Private key textarea (only when toggle is on) */}
                  {formData.config.useExistingKey && (
                    <div>
                      <label htmlFor="privateKeyContent" className={labelClass}>Private Key *</label>
                      <textarea
                        id="privateKeyContent"
                        value={formData.config.private_key_content || ''}
                        onChange={(e) => handleInputChange('config.private_key_content', e.target.value)}
                        placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                        rows={6}
                        className={errors.private_key_content ? inputErrorClass : inputNormalClass}
                      />
                      {errors.private_key_content && <span className={errorTextClass}>{errors.private_key_content}</span>}
                      <small className={smallTextClass}>Paste your RSA or ECDSA private key here.</small>
                    </div>
                  )}
                </div>
              )}

              {formData.config.auth_method === 'password' && (
                <div className="mb-4">
                  <label htmlFor="password" className={labelClass}>Password *</label>
                  <input
                    id="password"
                    type="password"
                    value={formData.config.password || ''}
                    onChange={(e) => handleInputChange('config.password', e.target.value)}
                    placeholder="Enter password"
                    className={errors.password ? inputErrorClass : inputNormalClass}
                  />
                  {errors.password && <span className={errorTextClass}>{errors.password}</span>}
                </div>
              )}
            </div>
          )}

          {(formData.hostingType === 'hosted' && formData.transferMethod === 'sftp') && (
            <div className={formSectionClass}>
              <h3 className={formSectionTitleClass}>Hosted Connection Settings</h3>

              <div className="mb-4">
                <label htmlFor="publicKeyContent" className={labelClass}>Partner's Public Key (optional)</label>
                <textarea
                  id="publicKeyContent"
                  value={formData.config.public_key_content || ''}
                  onChange={(e) => handleInputChange('config.public_key_content', e.target.value)}
                  placeholder="ssh-rsa AAAAB3NzaC1yc2E..."
                  rows={4}
                  className={errors.public_key_content ? inputErrorClass : inputNormalClass}
                />
                {errors.public_key_content && <span className={errorTextClass}>{errors.public_key_content}</span>}
              </div>

              <div className="mb-4">
                <label htmlFor="allowedIpRanges" className={labelClass}>Allowed IP Ranges (optional)</label>
                <input
                  id="allowedIpRanges"
                  type="text"
                  value={formData.config.allowed_ip_ranges?.join(', ') || ''}
                  onChange={(e) => handleInputChange('config.allowed_ip_ranges', e.target.value.split(',').map(ip => ip.trim()).filter(ip => ip))}
                  placeholder="192.168.1.0/24, 10.0.0.0/8"
                  className={inputNormalClass}
                />
              </div>
            </div>
          )}

          <div className={formSectionClass}>
            <h3 className={formSectionTitleClass}>Advanced Settings</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="mb-4">
                <label htmlFor="timeout" className={labelClass}>Timeout (seconds)</label>
                <input
                  id="timeout"
                  type="number"
                  value={formData.config.timeout || 30}
                  onChange={(e) => handleInputChange('config.timeout', parseInt(e.target.value))}
                  min="5"
                  max="300"
                  className={inputNormalClass}
                />
              </div>

              <div className="mb-4">
                <label htmlFor="retryAttempts" className={labelClass}>Retry Attempts</label>
                <input
                  id="retryAttempts"
                  type="number"
                  value={formData.config.retry_attempts || 3}
                  onChange={(e) => handleInputChange('config.retry_attempts', parseInt(e.target.value))}
                  min="0"
                  max="10"
                  className={inputNormalClass}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center">
                <label className="flex items-center mb-0 cursor-pointer text-slate-900 dark:text-slate-100">
                  <input
                    type="checkbox"
                    checked={formData.config.enable_encryption || false}
                    onChange={(e) => handleInputChange('config.enable_encryption', e.target.checked)}
                    className="w-auto mr-2 accent-primary-600"
                  />
                  Enable Encryption
                </label>
              </div>

              <div className="flex items-center">
                <label className="flex items-center mb-0 cursor-pointer text-slate-900 dark:text-slate-100">
                  <input
                    type="checkbox"
                    checked={formData.config.enable_compression || false}
                    onChange={(e) => handleInputChange('config.enable_compression', e.target.checked)}
                    className="w-auto mr-2 accent-primary-600"
                  />
                  Enable Compression
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2.5 pt-5 border-t border-slate-200 dark:border-slate-600">
            <button
              type="button"
              onClick={onClose}
              className="bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 border-none px-5 py-2.5 rounded cursor-pointer text-sm transition-colors hover:bg-slate-300 dark:hover:bg-slate-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-primary-600 text-white border-none px-5 py-2.5 rounded cursor-pointer text-sm transition-colors hover:bg-primary-700 disabled:bg-slate-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Connection'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateConnection;
